import type { Ref } from 'vue';
import { computed, ref, watch } from 'vue';

import type { Population } from '@/types/populations';
import {
  OtherPopulationType,
  StandardPopulationType,
} from '@/types/populations';

interface UsePopulationFormOptions {
  population: Ref<Population | null>;
  isEditMode: Ref<boolean>;
}

export function usePopulationForm({
  population,
  isEditMode,
}: UsePopulationFormOptions) {
  const selectedFeedId = ref<number | null | 'add-new-source'>(null);
  const selectedObject = ref<number | null>(null);
  const populationDescription = ref<string | undefined>(undefined);
  const populationName = ref<string | undefined>(undefined);
  const selectedPopType = ref<
    StandardPopulationType | OtherPopulationType | null
  >(null);

  watch(
    () => population.value,
    (newPopulation) => {
      if (
        newPopulation &&
        !(newPopulation as Population & { isEmpty?: boolean }).isEmpty
      ) {
        selectedFeedId.value = newPopulation.feed_id || selectedFeedId.value;
        selectedObject.value = newPopulation.source_id || null;
        populationDescription.value = newPopulation.description ?? undefined;
        populationName.value = newPopulation.name ?? undefined;
        if (selectedPopType.value === null || !isEditMode.value) {
          selectedPopType.value = newPopulation.segment ?? null;
        }
      }
    },
    { immediate: true },
  );

  const hasDetailsChanges = computed(() => {
    if (!population.value || !isEditMode.value) return false;

    const normalizeString = (
      value: string | null | undefined,
    ): string | undefined => {
      if (value === null || value === undefined || value === '') {
        return undefined;
      }
      return value;
    };

    const nameChanged =
      normalizeString(populationName.value) !==
      normalizeString(population.value.name);

    const descriptionChanged =
      normalizeString(populationDescription.value) !==
      normalizeString(population.value.description);

    const feedChanged = selectedFeedId.value !== population.value.feed_id;
    const objectChanged = selectedObject.value !== population.value.source_id;
    const popTypeChanged =
      selectedPopType.value !== (population.value.segment ?? null);

    return (
      nameChanged ||
      descriptionChanged ||
      feedChanged ||
      objectChanged ||
      popTypeChanged
    );
  });

  return {
    selectedFeedId,
    selectedObject,
    populationDescription,
    populationName,
    selectedPopType,
    hasDetailsChanges,
  };
}
